import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { Input } from '../../../components/core/Input';
import { PrimaryButton } from '../../../components/others/ButtonPrimary';
import LavorisService from '../../../services/LavorisService';
import StyledWrapper from './style';
import { IndividualEmployeeResponseService } from '../../../services/Company/Employee.type';
import Select from '../../../components/others/Select/Select';
import { InputEmail } from '../../../components/others/InputEmail';
import { PhoneNumber } from '../../../components/others/PhoneNumber';
import { DatePicker } from '../../../components/others/DatePicker';
import AddressAutocomplete from '../../../components/others/MapsAutocomplete';
import {
  nationalities,
  maritalStatuses,
  sexs
} from '../../../utils/dataOptionsUsers';
import CuilRadioInput from '../../../components/others/CuilRadioInput/CuilRadioInput';
import InputCuil from '../../../components/others/InputCuil/InputCuil';
import { useAlert } from '../../../hooks/useAlert';
import { getErrorMessage } from '../../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/combineReducer';
import InputRadio from '../../../components/others/InputRadio/InputRadio';

type TProps = {
  onClose: Function;
  refreshFunction?: Function;
};

export default function EditEmployee({ onClose, refreshFunction }: TProps) {
  const { readOnly } = useSelector((state: RootState) => state.app?.modalData)
 
  //useState
  const [employeeData, setEmployeeData] =
    useState<IndividualEmployeeResponseService>();
  const [isReady, setReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentNationality, setCurrentNat] = useState(true);
  const [defaultPosition, setDefaultPosition] = useState<string>();
  const [defaultMaritalStatus, setDefaultMaritalStatus] = useState<string>();
  const [defaultSex, setDefaultSex] = useState<string>();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'all'
  });

  // LAVORIS INSTANCE FOR SERVICES
  const LavorisInstance = LavorisService.getInstance();
  const employeeID = searchParams.get('rowId');


  const getAddressComponentByType = (
    type: string,
    locationObj: google.maps.places.PlaceResult,
    fullAdrss: boolean = true,
  ): string | null => {
    const addressComponents = locationObj.address_components;
    if (fullAdrss) {
      const adrsName = locationObj.formatted_address || ""
      return adrsName
    }
    if (addressComponents) {
      for (let i = 0; i < addressComponents.length; i++) {
        const component = addressComponents[i];
        if (component.types.includes(type)) {
          return component.long_name;
        }
      }
    }
    return null;
  };


  //Handle Address
  const handleSelectAddress = (
    addressObject: google.maps.places.PlaceResult,
  ) => {
    setValue(
      "fullAddress",
      getAddressComponentByType("route", addressObject, true) +
      " "
    );

    setValue(
      "city",
      getAddressComponentByType("locality", addressObject, false) || "",
    );
    setValue(
      "province",
      getAddressComponentByType("administrative_area_level_1", addressObject, false) ||
      "",
    );
    setValue(
      "postalCode",
      getAddressComponentByType("postal_code", addressObject, false) || "",
    );

    if (
      addressObject &&
      addressObject.geometry &&
      addressObject.geometry.location
    ) {
      setLat(addressObject.geometry.location.lat().toString() || "");
      setLng(addressObject.geometry.location.lng().toString() || "");
    }
  };

  // GET INDIVIDUAL EMPLOYEE'S PROFILE
  const getEmployeeProfile = async () => {
    try {
      const employeeProfile =
        await LavorisInstance.CompanyServices.getIndividualEmployee(
          employeeID || ' '
        );
      if (employeeProfile) {
        const nationalityValues = nationalities.map((nat) => nat.value);
        const isKnownNationality = nationalityValues.includes(
          employeeProfile.nationality
        );

        setEmployeeData(employeeProfile);

        const arrayName = employeeProfile.name.split(',');
        const first_name = arrayName[1]
          ? arrayName[1].trim()
          : arrayName[0].trim();
        const last_name = arrayName[1] ? arrayName[0].trim() : '';

        const dateObject = moment(
          `${employeeProfile.birthDate} GMT-3`,
          'DD/MM/YYYY Z'
        );
        const fechaFormateada = dateObject.format('YYYY-MM-DD[T]HH:mm:ss.SSSZ');
        employeeProfile.birthDate = fechaFormateada;

        reset({
          ...employeeProfile,
          position: employeeProfile.position,
          cuil: employeeProfile.cuil === 'Pendiente' ? '' : employeeProfile.cuil
        });

        setDefaultPosition(employeeProfile.position || 'default');
        setDefaultMaritalStatus(employeeProfile.maritalStatus || 'default');
        setDefaultSex(employeeProfile.sex || 'default');

        if (!isKnownNationality) {
          setValue('nationalityOther', employeeProfile.nationality);
          setValue('nationality', 'OTRO');
        }
        setCurrentNat(isKnownNationality);
        setValue('first_name', first_name);
        setValue('last_name', last_name);
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  const handleSelectNationality = (event: any) => {
    const isKnownNationality = event.target.value !== 'OTRO';
    setCurrentNat(isKnownNationality);
  };

  const cuil = watch('cuil') || '';
  const [hasCuil, setHasCuil] = useState(
    cuil !== '' && cuil !== undefined && cuil !== 'Pending'
  );
  const [dni, setDni] = useState(cuil.replace(/[^0-9]/g, '').substring(2, 10));

  useEffect(() => {
    setDni(cuil.replace(/[^0-9]/g, '').substring(2, 10));
    setHasCuil(cuil !== '');
  }, [cuil]);

  useEffect(() => {
    setValue('DNI', dni);
  }, [dni, setValue]);

  useEffect(() => {
    setHasCuil(cuil !== '' && cuil !== undefined && cuil !== 'Pending');
  }, [cuil]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getEmployeeProfile();
        setTimeout(() => {
          setReady(true);
        }, 300);
      } catch (e) {
        console.log(e);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: any) => {
    setIsLoading(true);

    let name = `${data.last_name.trim()}, ${data.first_name.trim()}`;

    const dateObj = new Date(data.birthDate);
    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();

    const formattedDate = `${day < 10 ? '0' : ''}${day}/${month < 10 ? '0' : ''
      }${month}/${year}`;

    let updatedata: any = {
      name: name.trim(),
      DNI: data.DNI.trim(),
      cuil: !hasCuil ? 'Pendiente' : data.cuil,
      birthDate: formattedDate,
      position: 'OTROS',
      positionName: data.positionName,
      fullAddress: data.fullAddress,
      city: data.city,
      supplementaryReference: data.supplementaryReference,
      province: data.province,
      postalCode: data.postalCode,
      phoneNumber: data.phoneNumber,
      email: data.email.trim(),
      sex: data.sex,
      maritalStatus: data.maritalStatus,
      isActive: data.isActive === 'Si' ? true : false,
      nationality:
        data.nationality === 'OTRO' ? data.nationalityOther : data.nationality
    };

    if (lat && lng) {
      updatedata.lat = lat;
      updatedata.lng = lng;
    }

    try {
      await LavorisInstance.CompanyServices.updateEmployee(updatedata, data.id);
      onClose();
      refreshFunction?.();
    } catch (err: any) {
      console.log(err);
      showAlert(getErrorMessage({ title: 'Error al editar el empleado', err }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isReady)
    return (
      <h1 className="w-full flex justify-center text-red-500 font-bolder animate-pulse">
        {' '}
        Cargando...{' '}
      </h1>
    );

  return (
    <div className="w-full h-full">
      <StyledWrapper.NameTitle>{employeeData?.name}</StyledWrapper.NameTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledWrapper.FormContainer>
          <Input
            type="text"
            label="Apellido"
            fieldName="last_name"
            register={register}
            error={errors?.last_name}
            required
            readOnly={readOnly}
          />
          <Input
            type="text"
            label="Nombre"
            fieldName="first_name"
            register={register}
            error={errors?.first_name}
            required
            readOnly={readOnly}
          />
          <CuilRadioInput hasCuil={hasCuil} setHasCuil={setHasCuil} />
          {hasCuil ? (
            <>
              <InputCuil
                label="CUIL -Sin espacios ni guiones-"
                fieldName="cuil"
                register={register}
                error={errors?.cuil}
                required={hasCuil}
                readOnly={readOnly}
              />
              <Input
                type="text"
                label="DNI"
                fieldName="DNI"
                register={register}
                error={errors?.DNI}
                readOnly={true}
                value={dni}
              />
            </>
          ) : (
            <Input
              type="text"
              label="DNI o Pasaporte"
              fieldName="DNI"
              register={register}
              error={errors?.DNI}
              required
              readOnly={readOnly}

            />
          )}
          <Select
            fieldName="nationality"
            label="Nacionalidad"
            options={nationalities}
            onChange={handleSelectNationality}
            register={register}
            error={errors?.nationality}
            required
            styles="rounded w-full h-[50px]"
            readOnly={readOnly}
          />
          {!currentNationality && (
            <>
              <p>
                Si su nacionalidad no se encuentra entre las opciones, por favor
                ingresela*
              </p>
              <Input
                type="text"
                fieldName="nationalityOther"
                label="Nacionalidad"
                register={register}
                error={errors?.nacionality}
                required
                readOnly={readOnly}
              />
            </>
          )}
          <DatePicker
            fieldName="birthDate"
            label={'Fecha de Nacimiento'}
            minDate={100}
            maxDate={18}
            control={control}
            required
            error={errors?.birthDate}
            readOnly={readOnly}
          />
          <Select
            fieldName="sex"
            label="Sexo"
            options={sexs}
            register={register}
            error={errors?.sex}
            required
            defaultValue={defaultSex}
            styles="rounded w-full h-[50px]"
            readOnly={readOnly}
          />
          <Select
            fieldName="maritalStatus"
            label="Estado Civil"
            options={maritalStatuses}
            register={register}
            error={errors?.maritalStatus}
            required
            styles="rounded w-full h-[50px]"
            defaultValue={defaultMaritalStatus}
            readOnly={readOnly}
          />
          <AddressAutocomplete
            onSelectAddress={handleSelectAddress}
            type="text"
            label="GEOLOCALIZAR DIRECCION (calle y numero unicamente)"
            fieldName="fullAddress"
            error={errors?.fullAddress}
            required
            defaultValue={employeeData?.fullAddress}
            readOnly={readOnly}
          />
          <Input
            type="text"
            label="Referencia Adicional (importante Ejemplo: Número de calle, Número de departamento,Número de piso  M:12 C:8, Barrio)"
            fieldName="supplementaryReference"
            register={register}
            error={errors?.supplementaryReference}
            //placeholder="Ejemplo: Número de calle, Número de departamento,Número de piso, M:12 C:8"
            style={{ width: 'auto', height: '150px' }}
            readOnly={readOnly}
          />
          <Input
            type="text"
            label="Código postal (opcional)"
            fieldName="postalCode"
            register={register}
            error={errors?.postalCode}
            readOnly={readOnly}
          // required
          />
          <PhoneNumber
            label="Teléfono"
            fieldName="phoneNumber"
            control={control}
            error={errors?.phoneNumber}
            required
          />
          <InputEmail
            label="E-mail"
            nameInput={'email'}
            register={register}
            required
            error={errors}
            readOnly={readOnly}
          />
          <Input
            type="text"
            label="Puesto"
            fieldName="positionName"
            register={register}
            error={errors?.positionName}
            required
            readOnly={readOnly}
          />
          <InputRadio
          fieldName="isActive"
          label="Activo"
          option1="Si"
          option2="No"
          register={register}
          defaultValue={employeeData?.isActive === true ? "Si" : "No"}
        />
          {/* <Select
            fieldName="position"
            label="Puesto"
            options={jobPositions}
            register={register}
            error={errors?.position}
            required
            defaultValue={defaultPosition}
            styles="rounded w-full h-[50px]"
          /> */}
          <div className="flex gap-6">
            {
              !readOnly && (
                <>
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      onClose();
                    }}
                    text="Cancelar"
                    styles="bg-white font-bold text-red-500 mt-8 border-red-500 border"
                  />

                  <PrimaryButton
                    type="submit"
                    text="Guardar cambios"
                    styles="bg-red-500 font-bold text-white mt-8"
                    disabled={isLoading}
                  />
                </>



              )
            }
            {
              readOnly && (
                <PrimaryButton
                  type="button"
                  onClick={() => onClose?.()}
                  text="Cerrar"
                  styles="bg-white font-bold text-red-500 mt-8 border-red-500 border"
                />
              )
            }


          </div>
        </StyledWrapper.FormContainer>
      </form>
    </div>
  );
}
